/**
 * components/RecipeEditModal.js
 * Edit a recipe in place: name, note, and both line lists (same pick-a-line UX as Transformasi, via the
 * shared LineEditor + ItemPickerModal). Type is FIXED on edit (label only). No "+ Buat SET baru" here —
 * pick existing items only. Seeds from a deep copy so Batal/Esc/click-outside discard edits cleanly.
 */
import React, { useMemo, useState } from "react";
import LineEditor from "./LineEditor";
import ItemPickerModal from "./ItemPickerModal";
import { useModalEsc, backdropClose } from "../utils/modalStack";

const TYPE_LABEL = { ASSEMBLE: "Rakit (Assemble)", TEARDOWN: "Urai (Teardown)" };

export default function RecipeEditModal({ recipe, items = [], onSave, onClose }) {
  const [name, setName] = useState(recipe.name || "");
  const [note, setNote] = useState(recipe.note || "");
  const [inputs, setInputs] = useState(() => (recipe.inputs || []).map((l) => ({ internalCode: l.internalCode, qty: l.qty })));
  const [outputs, setOutputs] = useState(() => (recipe.outputs || []).map((l) => ({ internalCode: l.internalCode, qty: l.qty })));
  const [picker, setPicker] = useState(null); // { side, lineIndex } | null
  const [err, setErr] = useState("");

  useModalEsc(onClose);

  const byCode = useMemo(() => new Map(items.map((it) => [it.internalCode, it])), [items]);

  // Picker ordering — smart-default, not a wall (same idea as Transformasi). Every item selectable.
  const isSetItem = (it) => it.isSet === true;
  const sortRanked = (rank) => [...items].sort((a, b) => rank(a) - rank(b));
  const inputItems = useMemo(() => sortRanked((it) => (isSetItem(it) ? 1 : 0)), [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const outputItems = useMemo(
    () => sortRanked(recipe.type === "TEARDOWN" ? (it) => (isSetItem(it) ? 1 : 0) : (it) => (isSetItem(it) ? 0 : 1)),
    [items] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const applyPick = (codes) => {
    if (!picker) return;
    const { side, lineIndex } = picker;
    const setLines = side === "input" ? setInputs : setOutputs;
    setLines((lines) => {
      if (lineIndex == null) {
        const existing = new Set(lines.map((l) => l.internalCode).filter(Boolean));
        const additions = codes.filter((c) => !existing.has(c)).map((c) => ({ internalCode: c, qty: 1 }));
        return [...lines.filter((l) => l.internalCode), ...additions];
      }
      return lines.map((l, i) => (i === lineIndex ? { ...l, internalCode: codes[0] } : l));
    });
    setPicker(null);
  };

  const cleanInputs = inputs.filter((l) => l.internalCode && Number(l.qty) > 0);
  const cleanOutputs = outputs.filter((l) => l.internalCode && Number(l.qty) > 0);

  const save = () => {
    const n = name.trim();
    if (!n) { setErr("Nama resep wajib diisi."); return; }
    if (cleanInputs.length === 0 || cleanOutputs.length === 0) {
      setErr("Perlu minimal satu input dan satu output."); return;
    }
    onSave({
      name: n,
      note: note.trim(),
      inputs: cleanInputs.map((l) => ({ internalCode: l.internalCode, qty: Number(l.qty) || 0 })),
      outputs: cleanOutputs.map((l) => ({ internalCode: l.internalCode, qty: Number(l.qty) || 0 })),
    });
  };

  return (
    <>
      <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
        <div className="modal-box modal-box--wide" style={{ position: "relative" }} onMouseDown={(e) => e.stopPropagation()}>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Tutup">×</button>
          <h2 className="modal-title">Ubah Resep</h2>
          <div className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>{TYPE_LABEL[recipe.type] || recipe.type}</div>

          {err && <div className="alert alert--error">{err}</div>}

          <div className="form-row">
            <label className="form-label" htmlFor="recipe-edit-nama">Nama resep (wajib)</label>
            <input id="recipe-edit-nama" className="form-input" value={name} autoFocus
              onChange={(e) => { setName(e.target.value); if (err) setErr(""); }} />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="recipe-edit-catatan">Catatan (opsional)</label>
            <input id="recipe-edit-catatan" className="form-input" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          <LineEditor title="Input" lines={inputs} byCode={byCode} onChange={setInputs}
            max={recipe.type === "TEARDOWN" ? 1 : undefined}
            maxHint={recipe.type === "TEARDOWN" ? "Urai memakai tepat satu barang sumber." : undefined}
            onAdd={() => setPicker({ side: "input", lineIndex: null })}
            onReplace={(i) => setPicker({ side: "input", lineIndex: i })} />
          <LineEditor title="Output" lines={outputs} byCode={byCode} onChange={setOutputs}
            onAdd={() => setPicker({ side: "output", lineIndex: null })}
            onReplace={(i) => setPicker({ side: "output", lineIndex: i })} />

          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose}>Batal</button>
            <button type="button" className="btn btn-primary" onClick={save}>Simpan</button>
          </div>
        </div>
      </div>

      {picker && (
        <ItemPickerModal
          items={picker.side === "input" ? inputItems : outputItems}
          bucket="PUSAT"
          multi={picker.lineIndex == null && !(picker.side === "input" && recipe.type === "TEARDOWN")}
          onConfirm={applyPick}
          onClose={() => setPicker(null)}
        />
      )}
    </>
  );
}
